/**
 * Card - Componente de visualización de preparación de pedido en mobile
 *
 * Este componente muestra la información de una preparación de pedido en formato de tarjeta,
 * respetando las columnas seleccionadas por el usuario a través del ColumnSelector.
 * La columna 'code' es fija y siempre se muestra.
 */
import { Badge } from '@shared/components/ui/badge';
import { Button } from '@shared/components/ui/button';
import { RenderItemProps } from '@shared/components/ui/VirtualList';
import { faCartShopping, faShoppingBag, faShoppingCart } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  getPreparationStatusColor,
  PreparationType,
  useActionButtonText,
  usePreparationStatusName,
  usePreparationTypeName,
} from '@modules/OrderPreparation/shared/config';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '@shared/hooks/usePermissions';
import { OrderPreparationModel } from '../../models/OrderPreparation';

// 🎯 Props para Card incluyendo las acciones
export interface CardProps extends RenderItemProps<OrderPreparationModel> {
  className?: string;
  showActions?: boolean;
  showIndex?: boolean;
  selectedColumns?: string[];
}

// 🎯 Componente de card individual para Preparación - SRP
export const Card = ({ item: orderpreparation, className = '', showActions = true, selectedColumns }: CardProps) => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const preparationTypeName = usePreparationTypeName(orderpreparation.type);
  const preparationStatusName = usePreparationStatusName(orderpreparation.status);
  const actionButtonText = useActionButtonText(orderpreparation.status);

  // 🎯 Verificar permisos de preparación
  const canPicking = hasPermission('preparations.picking');

  // 🎯 Helper para verificar si una columna está visible
  const isColumnVisible = (columnId: string) => {
    if (!selectedColumns) return true; // Si no hay selectedColumns, mostrar todo
    return selectedColumns.includes(columnId);
  };

  // Función para calcular la cantidad total de productos
  const getTotalQuantity = (lines: any[]) => {
    if (!lines || !Array.isArray(lines)) return 0;
    return lines.reduce((total, line) => total + (line.qty || 0), 0);
  };

  // Función para obtener el número de pedidos desde la API
  const getOrdersCount = (orderpreparation: OrderPreparationModel) => {
    return orderpreparation.total_orders || 0;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  return (
    <div className={`py-4 px-2 border-b border-card-border hover:bg-hover-press transition-colors ${className}`}>
      <div className="space-y-3">
        {/* Header con código y tipo - Code siempre visible, Type condicional */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-title">{orderpreparation.code}</span>
            {isColumnVisible('location') && (
              <>
                <span className="text-subtext">|</span>
                <span className="text-sm text-subtext inline-flex items-center gap-1">
                  {orderpreparation.location?.code ? (
                    <FontAwesomeIcon icon={faCartShopping} className="w-3.5 h-3.5 text-icons" />
                  ) : null}
                  {orderpreparation.location?.code || '-'}
                </span>
              </>
            )}
            {isColumnVisible('type') && (
              <>
                <span className="text-subtext">|</span>
                <span className="text-sm text-subtext">{preparationTypeName}</span>
              </>
            )}
          </div>
        </div>

        {/* Cantidades con iconos - Solo si las columnas están visibles */}
        {(isColumnVisible('quantity') || isColumnVisible('total_orders')) && (
          <div className="flex items-center gap-2">
            {isColumnVisible('quantity') && (
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center justify-center py-0.5 px-3 rounded-full border border-lines text-xs font-medium text-title">
                  <FontAwesomeIcon icon={faShoppingBag} className="w-3 h-3 mr-1" />
                  {getTotalQuantity(orderpreparation.lines || [])}
                </span>
              </div>
            )}
            {isColumnVisible('total_orders') && (
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center justify-center py-0.5 px-3 rounded-full border border-lines text-xs font-medium text-title">
                  <FontAwesomeIcon icon={faShoppingCart} className="w-3 h-3 mr-1" />
                  {getOrdersCount(orderpreparation)}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Estado y fecha - Condicionales según columnas seleccionadas */}
        {(isColumnVisible('status') || isColumnVisible('created_at')) && (
          <div className="flex items-center justify-between">
            {isColumnVisible('status') && (
              <Badge
                variant="outline"
                className={`text-xs bg-card-border border-0 ${getPreparationStatusColor(orderpreparation.status)}`}
              >
                {preparationStatusName}
              </Badge>
            )}
            {isColumnVisible('created_at') && (
              <span className="text-sm text-text">{formatDate(orderpreparation.created_at)}</span>
            )}
          </div>
        )}

        {/* Botón de acción - solo si tiene permiso de preparación */}
        {showActions && canPicking && (
          <Button
            variant="outline"
            className="w-full py-2 px-4 rounded-lg text-sm font-medium"
            onClick={() => {
              const type = orderpreparation.type;
              if (type === PreparationType.MASSIVE_PACKING) {
                navigate(`/order-preparation/massive-packing/${orderpreparation.id}`);
                return;
              }
              navigate(`/order-preparation/picking/${orderpreparation.id}`);
            }}
            title={actionButtonText}
            data-testid="view-details-button"
          >
            {actionButtonText}
          </Button>
        )}
      </div>
    </div>
  );
};
